/**
 * Offline Write Queue — Phase 1.8
 *
 * Persistent, IndexedDB-backed queue of pending POST/PUT/DELETE requests.
 * Survives page reload, replays automatically when the network comes back.
 *
 * Designed for "the teacher's wifi died mid-class" — a 22-student attendance
 * mark must not be lost just because the connection blipped.
 *
 * Usage:
 *   const id = await offlineQueue.enqueue({ method, url, body, headers });
 *   await offlineQueue.flush({ baseUrl, getAuthHeader });
 *   await offlineQueue.size();
 *
 * Storage: IndexedDB → `tarteeb-offline` → object store `writes`
 * Each entry: { id, method, url, body, headers, queuedAt, attempts, lastError }
 */

const DB_NAME = 'tarteeb-offline';
const DB_VERSION = 1;
const STORE = 'writes';

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(mode = 'readonly') {
  return openDB().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

function reqToPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const listeners = new Set();
function notify() {
  listeners.forEach((cb) => {
    try { cb(); } catch { /* listener error — non-fatal */ }
  });
}

export const offlineQueue = {
  /**
   * Subscribe to queue size changes. Returns an unsubscribe function.
   */
  subscribe(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },

  /**
   * Enqueue a request for later replay. Returns the new entry's id.
   */
  async enqueue({ method, url, body, headers }) {
    const store = await tx('readwrite');
    const id = await reqToPromise(store.add({
      method,
      url,
      body: body ?? null,
      headers: headers ?? null,
      queuedAt: Date.now(),
      attempts: 0,
      lastError: null,
    }));
    notify();
    return id;
  },

  /**
   * Return current queue entries (oldest first).
   */
  async list() {
    const store = await tx('readonly');
    return reqToPromise(store.getAll());
  },

  /**
   * Count queued items.
   */
  async size() {
    const store = await tx('readonly');
    return reqToPromise(store.count());
  },

  /**
   * Remove a single entry by id.
   */
  async remove(id) {
    const store = await tx('readwrite');
    await reqToPromise(store.delete(id));
    notify();
  },

  /**
   * Drop the whole queue (useful for "Discard pending" admin action).
   */
  async clear() {
    const store = await tx('readwrite');
    await reqToPromise(store.clear());
    notify();
  },

  /**
   * Update an entry — used to bump attempts/lastError on retry failure.
   */
  async update(id, patch) {
    const store = await tx('readwrite');
    const existing = await reqToPromise(store.get(id));
    if (!existing) return;
    await reqToPromise(store.put({ ...existing, ...patch }));
    notify();
  },

  /**
   * Flush the queue: replay each entry via fetch.
   * Returns { ok, failed }.
   *
   * @param {Object} opts
   * @param {string} opts.baseUrl       — API base URL (e.g. 'http://.../api')
   * @param {() => string|null} opts.getAuthHeader — returns 'Bearer ...' or null
   */
  async flush({ baseUrl, getAuthHeader } = {}) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return { ok: 0, failed: 0, skipped: true };
    }
    const entries = await this.list();
    if (entries.length === 0) return { ok: 0, failed: 0 };

    let ok = 0;
    let failed = 0;
    for (const entry of entries) {
      try {
        const url = entry.url.startsWith('http')
          ? entry.url
          : `${baseUrl || ''}${entry.url}`;
        const auth = getAuthHeader && getAuthHeader();
        const headers = {
          'Content-Type': 'application/json',
          ...(entry.headers || {}),
        };
        if (auth) headers.Authorization = auth;

        const resp = await fetch(url, {
          method: entry.method,
          headers,
          body: entry.body != null ? JSON.stringify(entry.body) : undefined,
        });

        // 2xx → consume; 4xx other than 408/429 → discard (won't succeed on retry);
        // 5xx + 408/429 → keep for next flush.
        if (resp.ok) {
          await this.remove(entry.id);
          ok += 1;
        } else if (resp.status >= 400 && resp.status < 500 && resp.status !== 408 && resp.status !== 429) {
          // Permanent failure — surface to UI but don't keep retrying forever.
          await this.update(entry.id, {
            attempts: (entry.attempts || 0) + 1,
            lastError: `HTTP ${resp.status}`,
          });
          failed += 1;
        } else {
          await this.update(entry.id, {
            attempts: (entry.attempts || 0) + 1,
            lastError: `HTTP ${resp.status}`,
          });
          failed += 1;
        }
      } catch (err) {
        // Network error during flush — stop, will retry on next 'online' event.
        await this.update(entry.id, {
          attempts: (entry.attempts || 0) + 1,
          lastError: err?.message || 'network error',
        });
        failed += 1;
        break;
      }
    }
    return { ok, failed };
  },
};

export default offlineQueue;
